import { create } from "zustand";

import { addConfiguredElementToDocument, addElementToDocument, duplicateElementInDocument, nudgeViewport, removeElementFromDocument, reorderElementInDocument, updateBindingsInDocument, updateElementInDocument, type HmiViewport } from "../editor/HmiEditorEngine";
import type { HmiBinding } from "../schema/hmi-bindings.schema";
import type { HmiCanvasDocument, HmiElement } from "../schema/hmi-canvas.schema";
import { cloneHmiDocument } from "../utils/default-document";

type HmiTool = "select" | "pan";

type HmiEditorState = {
  dashboardId: string | null;
  document: HmiCanvasDocument | null;
  savedDocument: HmiCanvasDocument | null;
  isDirty: boolean;
  selectedElementId: string | null;
  tool: HmiTool;
  viewport: HmiViewport;
  runtimeMode: boolean;
  saving: boolean;
  saveError: string | null;
  undoStack: HmiCanvasDocument[];
  redoStack: HmiCanvasDocument[];
  interactionSnapshot: HmiCanvasDocument | null;
  interactionDirty: boolean;
  initialize: (dashboardId: string, document: HmiCanvasDocument) => void;
  setTool: (tool: HmiTool) => void;
  setViewport: (delta: Partial<HmiViewport>) => void;
  setRuntimeMode: (value: boolean) => void;
  selectElement: (elementId: string | null) => void;
  addElement: (type: HmiElement["type"]) => void;
  addSymbolInstance: (definitionId: string) => void;
  updateElement: (elementId: string, updater: (element: HmiElement) => HmiElement) => void;
  removeSelectedElement: () => void;
  updateBindings: (elementId: string, updater: (bindings: HmiBinding[]) => HmiBinding[]) => void;
  updateDocument: (updater: (document: HmiCanvasDocument) => HmiCanvasDocument) => void;
  updateDocumentTransient: (updater: (document: HmiCanvasDocument) => HmiCanvasDocument) => void;
  duplicateSelectedElement: () => void;
  reorderSelectedElement: (direction: "forward" | "backward" | "front" | "back") => void;
  beginInteraction: () => void;
  updateElementTransient: (elementId: string, updater: (element: HmiElement) => HmiElement) => void;
  endInteraction: () => void;
  undo: () => void;
  redo: () => void;
  markSaving: (saving: boolean) => void;
  setSaveError: (message: string | null) => void;
  markSaved: (document: HmiCanvasDocument) => void;
};

const defaultViewport: HmiViewport = { x: 40, y: 40, zoom: 1 };

function documentsEqual(left: HmiCanvasDocument | null, right: HmiCanvasDocument | null): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export const useHmiEditorStore = create<HmiEditorState>((set) => ({
  dashboardId: null,
  document: null,
  savedDocument: null,
  isDirty: false,
  selectedElementId: null,
  tool: "select",
  viewport: defaultViewport,
  runtimeMode: false,
  saving: false,
  saveError: null,
  undoStack: [],
  redoStack: [],
  interactionSnapshot: null,
  interactionDirty: false,
  initialize: (dashboardId, document) =>
    set({
      dashboardId,
      document: cloneHmiDocument(document),
      savedDocument: cloneHmiDocument(document),
      isDirty: false,
      selectedElementId: null,
      tool: "select",
      viewport: defaultViewport,
      runtimeMode: false,
      saving: false,
      saveError: null,
      undoStack: [],
      redoStack: [],
      interactionSnapshot: null,
      interactionDirty: false,
    }),
  setTool: (tool) => set({ tool }),
  setViewport: (delta) => set((state) => ({ viewport: nudgeViewport(state.viewport, delta) })),
  setRuntimeMode: (runtimeMode) => set({ runtimeMode }),
  selectElement: (selectedElementId) => set({ selectedElementId }),
  addElement: (type) =>
    set((state) => {
      if (!state.document) {
        return state;
      }
      const nextDocument = addElementToDocument(state.document, type);
      const selectedElement = nextDocument.elements[nextDocument.elements.length - 1] ?? null;
      return {
        undoStack: [...state.undoStack, cloneHmiDocument(state.document)].slice(-30),
        redoStack: [],
        document: nextDocument,
        isDirty: true,
        selectedElementId: selectedElement?.id ?? state.selectedElementId,
      };
    }),
  addSymbolInstance: (definitionId) =>
    set((state) => {
      if (!state.document) {
        return state;
      }
      const nextDocument = addConfiguredElementToDocument(state.document, "symbol_instance", { symbolDefinitionId: definitionId });
      const selectedElement = nextDocument.elements[nextDocument.elements.length - 1] ?? null;
      return {
        undoStack: [...state.undoStack, cloneHmiDocument(state.document)].slice(-30),
        redoStack: [],
        document: nextDocument,
        isDirty: true,
        selectedElementId: selectedElement?.id ?? state.selectedElementId,
      };
    }),
  updateElement: (elementId, updater) =>
    set((state) => {
      if (!state.document) {
        return state;
      }
      return {
        undoStack: [...state.undoStack, cloneHmiDocument(state.document)].slice(-30),
        redoStack: [],
        document: updateElementInDocument(state.document, elementId, updater),
        isDirty: true,
      };
    }),
  removeSelectedElement: () =>
    set((state) => {
      if (!state.document || !state.selectedElementId) {
        return state;
      }
      return {
        undoStack: [...state.undoStack, cloneHmiDocument(state.document)].slice(-30),
        redoStack: [],
        document: removeElementFromDocument(state.document, state.selectedElementId),
        isDirty: true,
        selectedElementId: null,
      };
    }),
  updateBindings: (elementId, updater) =>
    set((state) => {
      if (!state.document) {
        return state;
      }
      return {
        undoStack: [...state.undoStack, cloneHmiDocument(state.document)].slice(-30),
        redoStack: [],
        document: updateBindingsInDocument(state.document, elementId, updater),
        isDirty: true,
      };
    }),
  updateDocument: (updater) =>
    set((state) => {
      if (!state.document) {
        return state;
      }
      const nextDocument = updater(state.document);
      return {
        undoStack: [...state.undoStack, cloneHmiDocument(state.document)].slice(-30),
        redoStack: [],
        document: {
          ...nextDocument,
          metadata: {
            ...nextDocument.metadata,
            updatedAt: new Date().toISOString(),
          },
        },
        isDirty: true,
      };
    }),
  updateDocumentTransient: (updater) =>
    set((state) => {
      if (!state.document) {
        return state;
      }
      const nextDocument = updater(state.document);
      return {
        document: {
          ...nextDocument,
          metadata: {
            ...nextDocument.metadata,
            updatedAt: new Date().toISOString(),
          },
        },
        isDirty: true,
        interactionDirty: true,
      };
    }),
  duplicateSelectedElement: () =>
    set((state) => {
      if (!state.document || !state.selectedElementId) {
        return state;
      }
      const nextDocument = duplicateElementInDocument(state.document, state.selectedElementId);
      const selectedElement = nextDocument.elements[nextDocument.elements.length - 1] ?? null;
      return {
        undoStack: [...state.undoStack, cloneHmiDocument(state.document)].slice(-30),
        redoStack: [],
        document: nextDocument,
        isDirty: true,
        selectedElementId: selectedElement?.id ?? state.selectedElementId,
      };
    }),
  reorderSelectedElement: (direction) =>
    set((state) => {
      if (!state.document || !state.selectedElementId) {
        return state;
      }
      return {
        undoStack: [...state.undoStack, cloneHmiDocument(state.document)].slice(-30),
        redoStack: [],
        document: reorderElementInDocument(state.document, state.selectedElementId, direction),
        isDirty: true,
      };
    }),
  beginInteraction: () =>
    set((state) => {
      if (!state.document || state.interactionSnapshot) {
        return state;
      }
      return {
        interactionSnapshot: cloneHmiDocument(state.document),
        interactionDirty: false,
      };
    }),
  updateElementTransient: (elementId, updater) =>
    set((state) => {
      if (!state.document) {
        return state;
      }
      return {
        document: updateElementInDocument(state.document, elementId, updater),
        isDirty: true,
        interactionDirty: true,
      };
    }),
  endInteraction: () =>
    set((state) => {
      if (!state.interactionSnapshot) {
        return state;
      }
      if (!state.interactionDirty) {
        return {
          interactionSnapshot: null,
          interactionDirty: false,
        };
      }
      return {
        undoStack: [...state.undoStack, state.interactionSnapshot].slice(-30),
        redoStack: [],
        interactionSnapshot: null,
        interactionDirty: false,
      };
    }),
  undo: () =>
    set((state) => {
      if (!state.document || state.undoStack.length === 0) {
        return state;
      }
      const previous = state.undoStack[state.undoStack.length - 1];
      if (!previous) {
        return state;
      }
      const nextDocument = cloneHmiDocument(previous);
      return {
        document: nextDocument,
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, cloneHmiDocument(state.document)].slice(-30),
        isDirty: !documentsEqual(nextDocument, state.savedDocument),
        selectedElementId: null,
        interactionSnapshot: null,
        interactionDirty: false,
      };
    }),
  redo: () =>
    set((state) => {
      if (!state.document || state.redoStack.length === 0) {
        return state;
      }
      const next = state.redoStack[state.redoStack.length - 1];
      if (!next) {
        return state;
      }
      const nextDocument = cloneHmiDocument(next);
      return {
        document: nextDocument,
        redoStack: state.redoStack.slice(0, -1),
        undoStack: [...state.undoStack, cloneHmiDocument(state.document)].slice(-30),
        isDirty: !documentsEqual(nextDocument, state.savedDocument),
        selectedElementId: null,
        interactionSnapshot: null,
        interactionDirty: false,
      };
    }),
  markSaving: (saving) => set({ saving }),
  setSaveError: (saveError) => set({ saveError }),
  markSaved: (document) =>
    set({
      document: cloneHmiDocument(document),
      savedDocument: cloneHmiDocument(document),
      isDirty: false,
      saving: false,
      saveError: null,
      undoStack: [],
      redoStack: [],
      interactionSnapshot: null,
      interactionDirty: false,
    }),
}));

export function useHmiIsDirty(): boolean {
  return useHmiEditorStore((state) => state.isDirty);
}

export function getSelectedElement(state: HmiEditorState): HmiElement | null {
  return state.document?.elements.find((element) => element.id === state.selectedElementId) ?? null;
}
